import { BaseClient } from '../base-client';
import type {
  ApprovalPolicy,
  CreateApprovalPolicyInput,
  ListApprovalPoliciesQuery,
  PatchApprovalPolicyInput,
  ReorderApprovalPoliciesInput,
} from './types';

type One = { data: ApprovalPolicy };
type ListResponse = {
  data: ApprovalPolicy[];
  meta: { total: number; limit: number; offset: number };
};

export class ApprovalPoliciesClient extends BaseClient {
  list(query: ListApprovalPoliciesQuery = {}): Promise<ListResponse> {
    return this.request('/api/approval-policies', { query: normalize(query) });
  }

  get(id: string): Promise<One> {
    return this.request(`/api/approval-policies/${encodeURIComponent(id)}`);
  }

  create(input: CreateApprovalPolicyInput): Promise<One> {
    return this.request('/api/approval-policies', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchApprovalPolicyInput): Promise<One> {
    return this.request(`/api/approval-policies/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: input,
    });
  }

  remove(id: string): Promise<One> {
    return this.request(`/api/approval-policies/${encodeURIComponent(id)}`, {
      method: 'DELETE',
      body: {},
    });
  }

  restore(id: string): Promise<One> {
    return this.request(`/api/approval-policies/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: {},
    });
  }

  /**
   * Persist a drag-reorder. Server rewrites every policy's `sort_order`
   * to its 1-based position in `orderedIds`; the response carries the
   * full re-ordered list so the cache can be replaced without a refetch.
   */
  reorder(input: ReorderApprovalPoliciesInput): Promise<{ data: ApprovalPolicy[] }> {
    return this.request('/api/approval-policies/reorder', {
      method: 'POST',
      body: input,
    });
  }
}

function normalize(q: object): Record<string, string | number | boolean | undefined> {
  const out: Record<string, string | number | boolean | undefined> = {};
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      out[k] = v;
    }
  }
  return out;
}
